/**
 * English copy. This file defines the shape of every dictionary: fr.ts must
 * match it key for key. Functions are used where a value is interpolated.
 */
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const en = {
  lang: {
    label: "Choose language",
    names: { fr: "Français", en: "English" },
  },

  format: {
    locale: "en-GB",
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    at: "at",
  },

  common: {
    somethingWrong: "Something went wrong.",
    loading: "Loading…",
    required: "Required field",
    allAreas: "All cities",
    perDay: "/day",
    perWeek: "/week",
    perWeekShort: "/wk",
    perMonth: "/month",
    verifiedCompany: "Verified company",
    vehiclesCount: (n: number) => `${n} ${n === 1 ? "vehicle" : "vehicles"}`,
    noCity: "No vehicles in that city yet",
    companyLabel: (id: number) => `Rental Company #${id}`,
    viewAll: "View all",
    sponsored: "Sponsored",
    photo: "photo",
    email: "E-mail",
    emailPlaceholder: "you@example.com",
    /** Example values shown in empty form fields. */
    ph: {
      firstName: "e.g. Karim",
      lastName: "e.g. Benali",
      phone: "e.g. 06 12 34 56 78",
      password: "Your password",
      newPassword: "At least 8 characters",
      confirmPassword: "Type the password again",
      companyName: "e.g. VTC Mobility Paris",
      displayName: "Name shown to drivers",
      siret: "912 345 678 00021",
      vat: "FR00 000000000",
      street: "e.g. 15 Avenue de la République",
      postalCode: "e.g. 75011",
      city: "e.g. Paris",
      region: "Region or state (optional)",
      country: "e.g. France",
      licenceNumber: "e.g. 12AB34567",
      year: "e.g. 2024",
      seats: "e.g. 5",
      colour: "e.g. Grey",
      mileage: "e.g. 32000",
      description: "Condition, equipment, what's included…",
      minDays: "e.g. 7",
      kmIncluded: "e.g. 1500",
      extraKm: "e.g. 0.12",
      insurance: "e.g. All-risk insurance included, €1,000 excess.",
      maintenance: "e.g. Servicing and tyres included. Replacement vehicle within 48 h.",
    },
    password: "Password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    phone: "Phone",
    login: "Log in",
    logout: "Log out",
    back: "Back",
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    edit: "Edit",
    publish: "Publish",
    send: "Send",
    view: "View",
    remove: "Remove",
    quickActions: "Quick actions",
    days: (n: number) => `${n} ${plural(n, "day", "days")}`,
    vehicles: (n: number) => `${n} ${plural(n, "vehicle", "vehicles")}`,
    availableUnits: (n: number) => `Available · ${n} ${plural(n, "vehicle", "vehicles")}`,
  },

  enums: {
    energy: {
      Electric: "Electric",
      Hybrid: "Hybrid",
      "Plug-in hybrid": "Plug-in hybrid",
      Diesel: "Diesel",
      Petrol: "Petrol",
    },
    transmission: { Automatic: "Automatic", Manual: "Manual" },
    category: {
      Sedan: "Sedan",
      "Premium sedan": "Premium sedan",
      Estate: "Estate",
      SUV: "SUV",
      Van: "Van",
    },
  },

  header: {
    nav: {
      vehicles: "Vehicles",
      howItWorks: "How it works",
      forCompanies: "For companies",
      pricing: "Company pricing",
      blog: "Blog",
    },
    companyArea: "Company area",
    myAccount: "My account",
    listVehicles: "List my vehicles",
    menu: "Menu",
    toggleMenu: "Toggle menu",
    accountMenu: "Account menu",
    tabs: {
      aria: "Quick navigation",
      home: "Home",
      findCar: "Find a car",
      listCar: "List a car",
      howItWorks: "How it works",
    },
  },
  captcha: {
    required: "Please complete the security check.",
  },
  changeEmail: {
    change: "Change",
    newEmail: "New e-mail address",
    password: "Current password",
    sendCode: "Send code",
    code: "Code received at the new address",
    confirm: "Confirm",
    cancel: "Cancel",
    hint: "We send a code to the new address. Your e-mail only changes once you enter it.",
  },
  notifications: {
    open: (n: number) => (n ? `Notifications, ${n} unread` : "Notifications"),
    title: "Notifications",
    markAll: "Mark all as read",
    empty: "No notifications yet.",
    loadMore: "Show older",
    loadError: "Couldn't load your notifications.",
    enablePush: "Get notified on this device",
    enablePushHint: "Booking requests, answers and messages, even when Le Coin VTC is closed.",
    enable: "Turn on",
    pushOn: "Push notifications are on for this device.",
    pushBlocked: "Notifications are blocked in your browser settings.",
    justNow: "Just now",
  },

  seo: {
    home: {
      title: "VTC Car Rental in France – LE COIN VTC",
      description: "Find VTC rental cars available by the week or month. Compare offers from professional rental companies on LE COIN VTC. Free search for drivers.",
      ogTitle: "VTC Car Rental in France – LE COIN VTC",
      ogDescription: "Find VTC rental cars available by the week or month. Compare offers from professional rental companies on LE COIN VTC. Free search for drivers.",
    },
    vehicles: {
      title: "VTC Cars for Rent: Compare Rental Offers – LE COIN VTC",
      description: "Explore VTC cars available for rent. Filter by city, price, brand, fuel type, and availability. No booking fees for drivers.",
      cityTitle: (city: string) => `VTC Rental Cars in ${city} – LE COIN VTC`,
      cityDescription: (city: string) => `VTC cars available for rent in ${city}. Filter by price, brand, fuel type, and availability. No booking fees for drivers.`,
    },
    vehicle: {
      title: (name: string, city: string) => `${name} VTC rental in ${city}`,
      description: (name: string, city: string, weekly: string, monthly: string) =>
        `Rent a ${name} for VTC work in ${city}: ${weekly}/week, ${monthly}/month, from a verified rental company. Request a booking for free on Le Coin VTC.`,
    },
    company: {
      title: (name: string) => `${name}: VTC rental company`,
      description: (name: string, count: number, area: string) =>
        `${name}, verified VTC rental company${area ? ` in ${area}` : ""}: ${count} vehicle${count === 1 ? "" : "s"} for rent, prices and driver reviews.`,
    },
    how: {
      title: "How to Rent a VTC Car – LE COIN VTC",
      description: "Learn how to rent a VTC car or list your vehicles on LE COIN VTC. Free registration, online booking requests, and direct rental company payments.",
    },
    partners: {
      title: "Rent Your Cars to VTC Drivers – LE COIN VTC",
      description: "Own a VTC car rental business? List vehicles for free, manage your fleet, and receive booking requests from professional drivers. 0% commission.",
      ogTitle: "Rent Your Cars to VTC Drivers – LE COIN VTC",
      ogDescription: "Own a VTC car rental business? List vehicles for free, manage your fleet, and receive booking requests from professional drivers. 0% commission.",
    },
    pricing: {
      title: "VTC Rental Company Pricing: Free Listings – LE COIN VTC",
      description: "Discover LE COIN VTC's free launch offer: unlimited vehicle listings, fleet management, and booking requests with no subscription or commission.",
    },
    contact: {
      title: "Contact",
      description: "Questions about a booking, your account or listing your vehicles? Contact the Le Coin VTC team.",
    },
    legal: { title: "Legal notice", description: "Legal notice of Le Coin VTC, the VTC vehicle rental marketplace." },
    privacy: { title: "Privacy policy", description: "How Le Coin VTC collects, uses and protects your personal data, and how to exercise your rights." },
    terms: { title: "Terms of use", description: "Terms of use of the Le Coin VTC marketplace for drivers and rental companies." },
    login: "Log in",
    signup: "Create an account",
    companySignup: "Create a rental company account",
    forgotPassword: "Forgot password",
    resetPassword: "New password",
    verifyEmail: "Verify your e-mail",
    driverArea: "Driver area",
    companyArea: "Company area",
    breadcrumbHome: "Home",
    breadcrumbVehicles: "Vehicles",
  },
  blog: {
    metaTitle: "Blog — Le Coin VTC",
    metaDescription: "Guides, costs and regulations for VTC drivers renting a vehicle.",
    title: "Le Coin VTC Blog",
    intro: "Guides, costs and regulations for VTC drivers renting a vehicle.",
    all: "All",
    search: "Search articles",
    empty: "No articles here yet.",
    error: "Couldn't load the articles. Please try again.",
    minutes: (n: number) => `${n} min read`,
    back: "All articles",
    notFound: "This article doesn't exist or is no longer published.",
    notTranslated: "This article is only available in French.",
    related: "Related articles",
    ctaTitle: "Looking for a VTC vehicle?",
    ctaText: "Compare vehicles from verified rental companies, from one day to one month.",
    ctaButton: "See vehicles",
    band: "Blog",
    youAreHere: "You are here:",
    home: "Home",
    by: "By",
    in: "In",
    views: (n: number) => `${n.toLocaleString("en-GB")} ${n === 1 ? "view" : "views"}`,
    faqTitle: "Frequently asked questions",
    tags: "Tags",
    tag: (t: string) => `Tag: ${t}`,
    clearFilter: "Clear filter",
    pagination: "Blog pages",
    previous: "Previous",
    next: "Next",
    pageOf: (n: number, total: number) => `Page ${n} of ${total}`,
    latest: "Latest articles",
    popular: "Most read",
    categories: "Categories",
    archives: "Archives",
    findTitle: "Find your VTC vehicle",
    findText: "Compare weekly and monthly rentals from verified providers in Île-de-France.",
    findButton: "Browse vehicles",
    authorRoles: {
      super_admin: "Platform admin at Le Coin VTC",
      editor: "Editor at Le Coin VTC",
      writer: "Writer at Le Coin VTC",
    } as Record<string, string>,
    ctas: {
      vehicles: { title: "Find a VTC vehicle near you", text: "Weekly and monthly rentals from verified providers in Île-de-France.", button: "Browse vehicles", href: "/vehicles" },
      list: { title: "Rent out your VTC vehicles", text: "List your fleet for free and reach verified VTC drivers.", button: "List my vehicles", href: "/partners" },
      companies: { title: "Free for rental companies during launch", text: "0% commission, unlimited listings, no card required.", button: "See company pricing", href: "/pricing" },
    },
  },
  footer: {
    faq: "FAQ",
    pricing: "Company pricing",
    blog: "Blog",
    legal: "Legal notice",
    privacy: "Privacy policy",
    terms: "Terms of use",
    contact: "Contact",
    prototype: "Prototype — fictitious companies and data",
  },

  pricingPage: {
    title: "VTC Rental Company Pricing",
    intro: "LE COIN VTC is completely free for all rental companies during our launch period. Publish unlimited vehicle listings, enjoy 0% commission on your rentals, and access our marketplace at no cost. Always free for VTC drivers.",
    launchBadge: "LAUNCH OFFER",
    launchTitle: "Free Launch Access",
    launchPrice: "€0",
    launchNote: "No subscription, no credit card required, and 0% commission.",
    launchCta: "Create a rental company account",
    launchFeatures: [
      "Unlimited active vehicle listings",
      "Unlimited booking requests",
      "Availability calendar and fleet management",
      "Messaging with VTC drivers",
      "Public rental company profile with customer reviews",
      "Email support",
    ],
    laterTitle: "Paid plans later",
    laterText:
      "Subscription plans will be introduced after the launch period. Every company will be notified at least 30 days before any change, and listings published during launch are never deleted.",
    sponsoredBadge: "Sponsored",
    sponsoredTitle: "Sponsored listings, available on every plan",
    sponsoredText: "Increase your vehicle’s visibility to VTC drivers: Sponsored badge and placement at the top of results.",
    sponsoredPrice: (days: number, price: string) => `${days} days — ${price}`,
    footnote:
      "Sponsored listings are a one-off payment with no automatic renewal by default. Change plan at any time; your existing listings are never deleted.",
  },

  legalPages: {
    lastUpdated: "Last updated: 1 October 2026",
    placeholder: "Placeholder text for the prototype. It will be replaced by the final version reviewed by our legal team.",
    legal: {
      title: "Legal Notice",
      // Final text (no prototype banner), with its own date and introduction
      final: true,
      updated: "Last updated: 16 October 2026",
      intro: [
        "This website and the LE COIN VTC platform are published and operated by ALVOS.",
      ],
      sections: [
        {
          title: "Publisher",
          text: "LE COIN VTC is a platform published and operated by ALVOS, a simplified joint-stock company (SAS) with variable share capital of €2,000, with a minimum capital of €100.\nRegistered office: 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nSIREN: 928 450 063\nSIRET: 928 450 063 00013\nRCS: 928 450 063 R.C.S. Bobigny\nVAT number: FR51 928450063\nPublication director: Malkiat Kaur\nContact: support@lecoinvtc.com",
        },
        {
          title: "Hosting",
          text: "The LE COIN VTC website is hosted by:\nHosting provider: Namecheap\nLegal company name: Namecheap, Inc.\nRegistered address: 4600 East Washington Street, Suite 305, Phoenix, AZ 85034, United States\nCountry: United States\nWebsite: https://www.namecheap.com/\nContact: https://www.namecheap.com/support/",
        },
        {
          title: "Intellectual Property",
          text: "The LE COIN VTC name, logo, website design, software, texts, graphics and other original content available on the platform are protected by applicable intellectual-property laws.\nUnless otherwise stated, these elements belong to ALVOS or are used with the permission of their respective owners.\nAny reproduction, distribution, modification or commercial use without prior authorisation is prohibited, except where permitted by law.",
        },
        {
          title: "Liability",
          text: "LE COIN VTC operates as an online marketplace connecting VTC drivers with vehicle Rental Providers.\nUnless expressly stated otherwise, ALVOS does not own the vehicles listed on the platform and is not the vehicle Rental Provider.\nRental Providers remain responsible for their vehicle listings, prices, availability, rental conditions, insurance and vehicles offered through the platform.\nDrivers remain responsible for providing accurate information, meeting applicable eligibility requirements and complying with the rental agreement concluded with the Rental Provider.\nFor further information about use of the platform, please refer to our Terms of Use.",
        },
        {
          title: "Personal Data and Cookies",
          text: "Personal data collected through LE COIN VTC is processed in accordance with our Privacy Policy and applicable data-protection law.\nInformation about cookies and similar technologies is available in our Cookie Policy and cookie settings, where applicable.\nUsers may contact us regarding privacy matters at:\nEmail: privacy@lecoinvtc.com",
        },
        {
          title: "Applicable Law",
          text: "This website and these Legal Notices are governed by French law.\nAny dispute will be subject to the competent courts in accordance with applicable French and European law.",
        },
      ],
    },
    privacy: {
      title: "Privacy Policy",
      // Final text (no prototype banner), with its own date and introduction
      final: true,
      updated: "Last updated: 16 October 2026",
      intro: [
        "This Privacy Policy explains how LE COIN VTC, a platform published and operated by ALVOS, collects, uses, stores and protects personal data when you visit our website, create an account or use our marketplace services.",
        "We are committed to protecting your privacy and processing personal data in accordance with applicable French and European data-protection laws, including the General Data Protection Regulation (GDPR).",
      ],
      // "## " lines are sub-headings, "- " lines bulleted lists
      sections: [
        {
          title: "Data Controller",
          text: "The data controller responsible for the processing described in this Privacy Policy is:\nALVOS, operator and publisher of LE COIN VTC\nRegistered office: 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nSIREN: 928 450 063\nSIRET: 928 450 063 00013\nRCS: 928 450 063 R.C.S. Bobigny\nVAT number: FR51 928450063\nPrivacy contact: privacy@lecoinvtc.com\nFor questions concerning your personal data or your privacy rights, you can contact us using the address above.",
        },
        {
          title: "Personal Data We Collect",
          text: "The information we collect depends on how you use LE COIN VTC and whether you are a visitor, Driver or Rental Provider.\n## Account Information\nWhen you create an account, we may collect:\n- First and last name\n- Email address\n- Telephone number\n- Password or authentication information\n- Account type\n- Profile information\n- Preferred language\n- Account creation date\n- Account status\n## Driver Information\nFor Driver accounts, we may collect information necessary to establish and verify eligibility, including:\n- Identity information\n- Date of birth, where required\n- Postal address\n- Driving licence information\n- VTC professional documents, where applicable\n- Identity documents\n- Verification documents\n- Account verification status\n- Other documents required for access to particular marketplace services\n## Rental Provider Information\nFor Rental Providers, we may collect:\n- Company or business name\n- Legal representative or contact person's details\n- Business address\n- SIREN, SIRET or other registration information\n- Company registration documents\n- Contact information\n- Business type\n- Account type\n- Verification documents\n- Payment or payout information, where applicable\nWhere an individual operates as a Rental Provider, we may collect identification and professional information necessary to verify the account.",
        },
        {
          title: "Vehicle Listing Data",
          text: "When a Rental Provider publishes a vehicle listing, we may process information such as:\n- Vehicle make and model\n- Registration-related information where required\n- Vehicle photographs\n- Vehicle characteristics\n- Rental price\n- Security deposit\n- Availability\n- Pickup information\n- Rental conditions\n- Insurance-related information provided by the Rental Provider\n- Listing creation and modification history\nSome listing information is made publicly visible because publication is necessary for operation of the marketplace.\nRental Providers should not publish unnecessary personal information within public vehicle listings.",
        },
        {
          title: "Booking and Rental Information",
          text: "When you submit, receive or manage a booking request, we may process:\n- Driver and Rental Provider identifiers\n- Vehicle concerned\n- Requested rental dates\n- Booking status\n- Rental price\n- Security deposit information\n- Booking history\n- Cancellation information\n- Payment status\n- Messages relating to the booking\n- Dispute or support information\n- Rental-related documents where applicable\nThis information is used to operate the marketplace and facilitate interactions between Drivers and Rental Providers.",
        },
        {
          title: "Messages and Communications",
          text: "When users communicate through LE COIN VTC, we may process:\n- Messages exchanged between users\n- Sender and recipient information\n- Date and time of messages\n- Attachments\n- Support requests\n- Complaints\n- Reports concerning users or listings\nWe may access communications when reasonably necessary to provide support, investigate suspected fraud, enforce our Terms of Use, resolve reported issues, protect users or comply with legal requirements.\nUsers should not use the messaging system to send unnecessary sensitive or confidential personal information.",
        },
        {
          title: "Location Data",
          text: "LE COIN VTC may process location-related information when this is necessary for particular marketplace features.\nThis may include:\n- City entered during a vehicle search\n- Pickup location\n- Location attached to a vehicle listing\n- Approximate location based on search criteria\n- Location information voluntarily provided by users\nIf a future feature requires access to the precise location of a user's device, appropriate information and permissions will be provided before such access takes place.\nWe do not continuously track users' movements unless a specific feature clearly informs the user and has the appropriate legal basis.",
        },
        {
          title: "Payment Information",
          text: "Where payments, deposits, subscriptions, promotional services or Rental Provider payouts are processed through LE COIN VTC, payment services may be provided by an authorised third-party payment provider.\nDepending on the service, information may include:\n- Payer or payee identity\n- Transaction amount\n- Payment status\n- Payment date\n- Refund information\n- Payout information\n- Billing details\n- Limited payment-method information\nFull payment-card information should normally be processed directly by the authorised payment provider and not stored by LE COIN VTC.\nPayment provider: Stripe (https://stripe.com/privacy)\nThe payment provider may process personal data under its own privacy terms.",
        },
        {
          title: "Technical and Usage Data",
          text: "When you visit or use LE COIN VTC, we may automatically collect certain technical information, including:\n- IP address\n- Device type\n- Browser type and version\n- Operating system\n- Login history\n- Date and time of access\n- Pages or features used\n- Technical logs\n- Security events\n- Error information\n- Cookie identifiers where applicable\nWe use this information for security, fraud prevention, platform operation, troubleshooting and, where permitted, service analytics.",
        },
        {
          title: "Why We Use Your Personal Data",
          text: "We process personal data only where we have a valid legal basis.\n## To Create and Manage Your Account\nWe use your information to:\n- Create your account\n- Authenticate you\n- Maintain your profile\n- Provide account functionality\n- Enable marketplace features\nLegal basis: Performance of a contract or steps taken at your request before entering into a contract.\n## To Verify Users\nWe may use identification and professional documents to:\n- Verify Drivers\n- Verify Rental Providers\n- Reduce fraudulent accounts\n- Protect marketplace users\n- Check eligibility for certain services\nLegal basis: Performance of the platform contract, legitimate interests in marketplace security and fraud prevention, and legal obligations where applicable.\n## To Provide Marketplace Services\nWe process information to:\n- Publish vehicle listings\n- Display search results\n- Submit and receive booking requests\n- Facilitate communication\n- Manage bookings\n- Provide user dashboards\n- Deliver platform functionality\nLegal basis: Performance of a contract.\n## To Process Payments\nWhere applicable, information is processed to:\n- Collect rental-related amounts\n- Process platform fees\n- Process subscriptions\n- Handle paid visibility services\n- Facilitate Rental Provider payouts\n- Manage refunds\nLegal basis: Performance of a contract and compliance with applicable financial, accounting or tax obligations.\n## To Prevent Fraud and Protect the Platform\nWe may process data to:\n- Detect suspicious accounts\n- Prevent fraudulent documents\n- Investigate payment abuse\n- Protect users\n- Secure the website and applications\n- Enforce our Terms of Use\nLegal basis: Our legitimate interests in protecting LE COIN VTC, its users and marketplace integrity.\n## To Provide Customer Support\nWe process communications and account information to respond to:\n- Support requests\n- Complaints\n- Technical problems\n- Account questions\n- Booking-related issues\n- Reports concerning users or listings\nLegal basis: Performance of a contract and our legitimate interests in providing effective customer service.\n## To Meet Legal Obligations\nWe may process and retain information when required for:\n- Accounting\n- Tax requirements\n- Fraud investigations\n- Legal proceedings\n- Regulatory requests\n- Law-enforcement requests\n- Compliance with applicable laws\nLegal basis: Legal obligation.\n## Marketing Communications\nWith your consent where required, we may send information about:\n- LE COIN VTC updates\n- New marketplace features\n- Commercial offers\n- Promotions\n- Relevant platform services\nYou can unsubscribe from marketing communications at any time.\nLegal basis: Consent where required by applicable law.\nService messages concerning your account, security, bookings or important changes to the platform are not marketing communications.",
        },
        {
          title: "Who We Share Personal Data With",
          text: "We do not sell your personal data.\nWe may share information only where necessary with the following categories of recipients.\n## Other Marketplace Users\nInformation necessary for a booking or marketplace interaction may be shared between a Driver and a Rental Provider.\nFor example, following an appropriate stage of the booking process, relevant contact, profile or verification information may be made available where required to facilitate the rental.\n## Service Providers\nWe may use trusted service providers for:\n- Website hosting\n- Cloud infrastructure\n- Email delivery\n- SMS or notifications\n- Identity verification\n- Payment processing\n- Customer support\n- Cybersecurity\n- Data storage\n- Analytics, where applicable\nThese providers may process personal data only to the extent necessary to provide their services and according to applicable contractual and data-protection requirements.\n## Authorities\nWe may disclose personal data to courts, regulators, law-enforcement authorities or other competent authorities when required by law or where legally necessary to establish, exercise or defend legal rights.\n## Professional Advisers\nWhere necessary, information may be shared with lawyers, accountants, auditors, insurers or other professional advisers subject to appropriate confidentiality obligations.",
        },
        {
          title: "International Data Transfers",
          text: "Whenever possible, LE COIN VTC uses service providers that process personal data within France or the European Economic Area.\nHowever, some technology providers may process or make personal data accessible from countries outside the European Economic Area.\nWhere such transfers occur, we will use an appropriate legal mechanism where required, such as:\n- an adequacy decision adopted by the European Commission;\n- Standard Contractual Clauses;\n- another legally recognised transfer mechanism; or\n- another applicable GDPR safeguard.\nUsers may contact us for further information about safeguards applicable to international transfers concerning their personal data.",
        },
        {
          title: "How Long We Keep Personal Data",
          text: "We retain personal data only for as long as necessary for the purposes for which it was collected and to comply with applicable legal obligations.\nRetention periods may depend on the type of information and the purpose of processing.\n## Account Data\nAccount information is normally kept while the account remains active and may be retained for an additional period where necessary to deal with disputes, fraud, contractual obligations or legal claims.\n## Verification Documents\nIdentity and verification documents are retained only for the period necessary for verification, security, fraud prevention or applicable legal obligations.\nWhere the original document no longer needs to be retained, it should be deleted or appropriately restricted.\n## Booking and Transaction Records\nBooking, payment and transaction information may be retained for the period required to meet contractual, accounting, tax, fraud-prevention and legal obligations.\n## Messages and Support Records\nCommunications may be retained for the period necessary to provide the service, resolve disputes, maintain security or establish legal rights.\n## Technical Logs\nSecurity and technical logs are retained for an appropriate limited period depending on the purpose for which they are collected.\nWhen data is no longer required, it is deleted, anonymised or archived where retention is legally required.",
        },
        {
          title: "Account Closure and Data Deletion",
          text: "Closing an account does not necessarily result in the immediate deletion of all information.\nCertain information may need to be retained after account closure where necessary for:\n- existing rentals or bookings;\n- outstanding payments;\n- disputes;\n- fraud prevention;\n- accounting and taxation;\n- legal claims; or\n- compliance with legal obligations.\nInformation that no longer has a lawful purpose will be deleted or anonymised in accordance with our retention procedures.",
        },
        {
          title: "Your Data Protection Rights",
          text: "Subject to the conditions provided by applicable law, you may have the right to:\n## Right of Access\nRequest confirmation that we process your personal data and obtain a copy of that data.\n## Right to Rectification\nRequest correction of inaccurate or incomplete personal information.\n## Right to Erasure\nRequest deletion of your personal data where the conditions for deletion are met.\n## Right to Restriction\nRequest temporary restriction of certain processing activities.\n## Right to Data Portability\nReceive certain personal data in a structured, commonly used and machine-readable format where the right applies.\n## Right to Object\nObject to processing based on our legitimate interests in circumstances provided by law.\nYou may object to direct marketing at any time.\n## Right to Withdraw Consent\nWhere processing is based on your consent, you may withdraw that consent at any time.\nWithdrawal does not affect the lawfulness of processing carried out before consent was withdrawn.",
        },
        {
          title: "How to Exercise Your Rights",
          text: "To exercise a data-protection right, contact:\nEmail: privacy@lecoinvtc.com\nPostal address: ALVOS – LE COIN VTC, 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nPlease clearly describe your request.\nWe may request additional information where reasonably necessary to verify your identity before responding to a request involving personal data.\nWe will respond within the period required by applicable data-protection law.",
        },
        {
          title: "Right to Lodge a Complaint",
          text: "If you believe that your personal data has been processed in violation of applicable data-protection law, you may lodge a complaint with the French data-protection authority:\nCommission Nationale de l'Informatique et des Libertés (CNIL), 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France\nYou may also contact us first so that we can attempt to resolve your concern directly.",
        },
        {
          title: "Data Security",
          text: "LE COIN VTC takes appropriate technical and organisational measures designed to protect personal data against:\n- unauthorised access;\n- accidental loss;\n- alteration;\n- disclosure;\n- misuse;\n- destruction; and\n- unlawful processing.\nMeasures may include access controls, authentication, secure communications, technical monitoring, backups and restrictions on access to personal information.\nNo online system can provide absolute security, and users are also responsible for keeping their passwords and account credentials confidential.\nIf you believe your account has been compromised, contact LE COIN VTC immediately.",
        },
        {
          title: "Automated Decision-Making",
          text: "LE COIN VTC does not currently intend to make decisions producing legal or similarly significant effects solely through automated processing unless users are specifically informed otherwise.\nAutomated tools may nevertheless be used to assist with:\n- fraud detection;\n- security monitoring;\n- spam prevention;\n- suspicious activity identification;\n- listing moderation; or\n- marketplace optimisation.\nWhere applicable law grants specific rights concerning automated decision-making, those rights will be respected.",
        },
        {
          title: "Reviews and Public Information",
          text: "Some information submitted to LE COIN VTC may become publicly visible.\nThis may include:\n- Rental Provider profile information\n- Vehicle listings\n- Vehicle photographs\n- Public identification information assigned by the platform\n- Reviews and ratings\n- Other information specifically identified as public\nUsers should not publish personal information that is unnecessary for the marketplace.\nLE COIN VTC may moderate or remove public information where required by law or the Terms of Use.",
        },
        {
          title: "Cookies and Similar Technologies",
          text: "LE COIN VTC may use cookies and similar technologies for different purposes.\n## Strictly Necessary Cookies\nThese may be used to:\n- keep users logged in;\n- maintain account sessions;\n- remember security settings;\n- protect against fraud;\n- remember cookie preferences; and\n- provide essential website functionality.\nThese technologies may be used without consent where permitted by applicable law.\n## Analytics Cookies\nWhere analytics tools require consent, they will only be activated after the user provides consent.\nSome audience-measurement technologies may qualify for an exemption where they meet applicable legal requirements.\n## Advertising and Marketing Cookies\nIf LE COIN VTC introduces advertising, retargeting, social-media tracking or similar technologies requiring consent, they will not be activated until the user has provided valid consent.\nUsers will be able to refuse or withdraw consent through the available cookie controls.\nFor additional information, please refer to our Cookie Policy and cookie-settings interface.",
        },
        {
          title: "Third-Party Websites and Services",
          text: "LE COIN VTC may contain links to external websites or integrate third-party services.\nWe are not responsible for the privacy practices of independent third parties.\nUsers should review the privacy policies of external services before providing personal information to them.",
        },
        {
          title: "Children's Privacy",
          text: "LE COIN VTC is a professional marketplace intended for adults and businesses.\nThe platform is not intended for persons under the age of 18.\nWe do not knowingly create marketplace accounts for minors.\nIf we become aware that a minor has provided personal data in circumstances where an account is not permitted, appropriate steps may be taken to delete or restrict the information.",
        },
        {
          title: "Changes to This Privacy Policy",
          text: "We may update this Privacy Policy when:\n- marketplace functionality changes;\n- new services are introduced;\n- our data-processing practices change;\n- service providers change; or\n- applicable legal requirements change.\nThe updated version will display a revised “Last updated” date.\nWhere a change materially affects the way we process personal data, we will provide additional notice where required.",
        },
        {
          title: "Contact Us",
          text: "For questions about this Privacy Policy or the processing of your personal data, contact:\nLE COIN VTC, published and operated by ALVOS\n1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nSIREN: 928 450 063\nRCS: 928 450 063 R.C.S. Bobigny\nPrivacy email: privacy@lecoinvtc.com",
        },
      ],
    },
    terms: {
      title: "Terms of Use",
      // Final text (no prototype banner), with its own date and introduction
      final: true,
      updated: "Last updated: 16 October 2026",
      intro: [
        "These Terms of Use govern access to and use of the LE COIN VTC website, applications and marketplace services.",
        "By creating an account, publishing a vehicle listing, submitting a booking request or otherwise using LE COIN VTC, you agree to these Terms of Use.",
        "LE COIN VTC is a platform published and operated by ALVOS, a simplified joint-stock company (SAS), registered at 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France, SIREN 928 450 063, RCS Bobigny 928 450 063.",
      ],
      // Lines starting with "- " are shown as a bulleted list
      sections: [
        {
          title: "Purpose of the Platform",
          text: "LE COIN VTC is an online marketplace designed to connect:\n- professional or eligible VTC drivers looking for vehicles to rent; and\n- professional or authorised vehicle rental providers offering vehicles suitable for rental.\nLE COIN VTC provides a digital intermediation service allowing users to search for vehicles, publish listings, communicate, submit booking requests and, where available, use payment or related marketplace services.\nUnless expressly stated otherwise, LE COIN VTC is not the owner, seller or rental provider of vehicles listed on the platform.\nThe vehicle rental agreement is concluded directly between the driver and the rental provider.",
        },
        {
          title: "Eligibility and Accounts",
          text: "Users must provide accurate, complete and up-to-date information when creating and maintaining an account.\nA user may create an account as a:\n- Driver: a person seeking to rent a vehicle for authorised professional use.\n- Rental Provider: a company, professional or authorised individual legally entitled to offer vehicles for rent.\nUsers are responsible for maintaining the confidentiality of their login credentials and for all activity carried out through their account.\nAccounts may not be transferred, sold or shared with another person without authorisation from LE COIN VTC.\nPublic visitors may browse available vehicle listings. An eligible Driver account is required to submit a reservation or access features restricted to registered users.",
        },
        {
          title: "Account and Document Verification",
          text: "Creating an account does not automatically mean that the account is verified.\nLE COIN VTC may request documents after registration in order to verify the identity, professional status or eligibility of a user.\nDepending on the account type, requested documents may include identification, business registration information, professional documents, driving licence information or other supporting documents.\nUsers must provide genuine, valid and up-to-date documents.\nLE COIN VTC may reject, restrict or suspend verification where documents are incomplete, expired, inconsistent, suspected to be fraudulent or do not meet platform requirements.\nVerification by LE COIN VTC does not constitute a guarantee of a user's financial reliability, vehicle condition, legal compliance or future conduct.",
        },
        {
          title: "Vehicle Listings",
          text: "Rental Providers are solely responsible for the accuracy and legality of their listings.\nA listing should accurately describe the vehicle and, where applicable, include information such as:\n- vehicle make and model;\n- photographs;\n- rental price;\n- security deposit;\n- availability;\n- rental duration;\n- pickup conditions;\n- mileage conditions;\n- insurance information;\n- eligibility requirements; and\n- any additional rental conditions.\nRental Providers must not publish misleading, fraudulent, duplicate or unlawful listings.\nA Rental Provider must have the legal right and authority to offer every vehicle published through its account.\nLE COIN VTC may request additional information, correct obvious display errors, reject a listing or remove a listing that does not comply with these Terms or applicable law.",
        },
        {
          title: "Vehicle and Rental Provider Responsibilities",
          text: "The Rental Provider remains responsible for the vehicle offered through the platform.\nThe Rental Provider must ensure, as applicable, that the vehicle:\n- can legally be rented;\n- is properly registered;\n- has the documents required for its use;\n- is maintained in a roadworthy condition;\n- has appropriate insurance for the proposed rental and intended use; and\n- complies with applicable legal and regulatory requirements.\nAny vehicle-specific conditions, insurance restrictions, security deposits, mileage limits or other obligations must be communicated to the Driver before the rental is confirmed.\nLE COIN VTC does not replace the Rental Provider's legal, insurance, maintenance or contractual responsibilities.",
        },
        {
          title: "Driver Responsibilities",
          text: "Drivers are responsible for ensuring that they meet the conditions required to rent and use a listed vehicle.\nDrivers must provide accurate information and valid documents requested by LE COIN VTC or the Rental Provider.\nThe Driver is responsible for:\n- holding a valid driving licence appropriate for the vehicle;\n- meeting applicable professional requirements for the intended use of the vehicle;\n- complying with the rental agreement;\n- respecting insurance conditions;\n- paying agreed rental charges, deposits and applicable fees;\n- using the vehicle lawfully and responsibly; and\n- returning the vehicle according to the agreed rental conditions.\nA Driver must not use the platform or a rented vehicle for unlawful, fraudulent or unauthorised purposes.",
        },
        {
          title: "Booking and Rental Agreement",
          text: "Submitting a booking request does not automatically create a rental agreement.\nA booking becomes confirmed only when the Rental Provider accepts the request and any required booking conditions, payments or deposits have been completed.\nThe final rental relationship is between the Driver and the Rental Provider.\nThe Rental Provider may require the Driver to sign a separate rental contract before or at vehicle handover.\nThe rental contract may specify additional conditions including:\n- rental duration;\n- payment schedule;\n- security deposit;\n- mileage;\n- insurance;\n- authorised drivers;\n- vehicle use;\n- cancellation;\n- vehicle return; and\n- damage responsibilities.\nWhere there is a conflict regarding the vehicle rental itself, the applicable rental agreement between the Driver and Rental Provider remains the primary contractual document for that rental, subject to applicable law.",
        },
        {
          title: "Prices, Payments and Security Deposits",
          text: "Rental Providers are responsible for setting their vehicle rental prices unless otherwise expressly stated by LE COIN VTC.\nThe applicable rental price, security deposit and any additional charges should be displayed or communicated before the booking is confirmed.\nWhere LE COIN VTC provides online payment functionality, payments may be processed by an authorised third-party payment service provider.\nLE COIN VTC may facilitate the collection or transfer of amounts between Drivers and Rental Providers without becoming the vehicle Rental Provider.\nAny applicable platform fee, booking fee, subscription fee or paid promotional service will be shown before the user commits to the relevant paid service.\nUsers remain responsible for taxes and other obligations applicable to their own professional activities.",
        },
        {
          title: "Cancellations, Refunds and No-Shows",
          text: "Cancellation and refund conditions applicable to a vehicle rental must be communicated before booking confirmation.\nWhere a Rental Provider establishes its own cancellation conditions, the Rental Provider is responsible for clearly communicating those conditions to the Driver.\nWhere LE COIN VTC introduces a platform-wide cancellation or refund policy, that policy will be displayed during the booking process and will form part of the applicable booking conditions.\nUsers must not repeatedly make bookings that they do not intend to honour.\nLE COIN VTC may take action against accounts showing fraudulent bookings, repeated unjustified cancellations, payment abuse or other misuse of the platform.",
        },
        {
          title: "Pricing of LE COIN VTC Services",
          text: "During the launch period, LE COIN VTC may provide some or all standard services free of charge.\nLE COIN VTC may subsequently introduce:\n- subscription plans;\n- limits on active vehicle listings;\n- premium features;\n- paid promotional options; or\n- other marketplace services.\nAny applicable price will be clearly displayed before purchase.\nMaterial pricing or contractual changes affecting existing professional users will be communicated in advance in accordance with applicable law and these Terms.",
        },
        {
          title: "Listing Ranking and Search Results",
          text: "Vehicle listings may be displayed according to factors such as:\n- relevance to the user's search;\n- selected location;\n- vehicle characteristics;\n- price;\n- availability;\n- listing completeness;\n- date of publication or update; and\n- other filters selected by the user.\nLE COIN VTC may also offer paid promotional visibility such as “Featured” or “Urgent” placement.\nWhere payment influences a vehicle's visibility or ranking, the promoted nature of the listing will be clearly identified.\nPayment for increased visibility does not constitute an endorsement, certification or guarantee of the Rental Provider or vehicle by LE COIN VTC.",
        },
        {
          title: "Reviews and Ratings",
          text: "LE COIN VTC may allow Drivers and Rental Providers to leave reviews or ratings following interactions or rentals.\nReviews must:\n- relate to a genuine experience;\n- be accurate and made in good faith;\n- not contain defamatory, discriminatory, threatening or unlawful content;\n- not disclose confidential or personal information without permission; and\n- not be manipulated through false accounts or artificial reviews.\nLE COIN VTC may moderate, restrict or remove reviews that breach these Terms or applicable law.",
        },
        {
          title: "Prohibited Use",
          text: "Users must not use LE COIN VTC to engage in fraud, identity theft, document falsification, unlawful vehicle rental, payment abuse, harassment, discrimination, unauthorised data collection, spam or any other illegal activity.\nUsers must not attempt to interfere with the security, availability or operation of the platform.\nAutomated scraping, unauthorised copying of listings, creation of false accounts or attempts to bypass platform security measures may result in restriction or suspension.",
        },
        {
          title: "Reporting Listings or Users",
          text: "Users may report a listing, account, review or other content that they believe is fraudulent, illegal, misleading or contrary to these Terms.\nLE COIN VTC may review reported content and request supporting information.\nDepending on the circumstances, LE COIN VTC may remove content, restrict a listing, request corrections, suspend an account or take any other proportionate action permitted by law.",
        },
        {
          title: "Account Restriction, Suspension and Termination",
          text: "LE COIN VTC may restrict, suspend or terminate an account or listing for legitimate reasons including:\n- false or misleading information;\n- fraudulent documents;\n- unlawful listings;\n- non-payment of applicable fees;\n- repeated cancellations or booking abuse;\n- misuse of reviews;\n- security threats;\n- violation of these Terms;\n- risk to users or the platform; or\n- legal or regulatory requirements.\nWhere required by applicable law, LE COIN VTC will provide the affected professional user with the reason for the restriction, suspension or termination and an opportunity to raise a complaint.\nUsers may also request closure of their account, subject to outstanding bookings, payments, disputes and legal retention requirements.",
        },
        {
          title: "Relationship Between LE COIN VTC and Users",
          text: "LE COIN VTC acts principally as an intermediary connecting Drivers and Rental Providers.\nUnless expressly stated for a particular service, LE COIN VTC:\n- does not own listed vehicles;\n- does not determine the condition of vehicles;\n- does not act as the vehicle Rental Provider;\n- does not employ Drivers or Rental Providers; and\n- is not automatically a party to the vehicle rental agreement.\nEach Rental Provider remains responsible for its vehicle, listing, rental contract, legal obligations, insurance and service provided to the Driver.\nEach Driver remains responsible for complying with the rental agreement and applicable professional and legal requirements.",
        },
        {
          title: "Platform Availability and Liability",
          text: "LE COIN VTC aims to provide a reliable platform but does not guarantee uninterrupted or error-free access.\nThe service may occasionally be unavailable because of maintenance, technical problems, security incidents or circumstances outside LE COIN VTC's reasonable control.\nTo the extent permitted by applicable law, LE COIN VTC is not responsible for losses resulting solely from:\n- inaccurate information supplied by another user;\n- the condition or performance of a rented vehicle;\n- conduct of a Driver or Rental Provider;\n- a Rental Provider's cancellation;\n- disputes arising from the rental agreement; or\n- circumstances beyond LE COIN VTC's reasonable control.\nNothing in these Terms excludes or limits liability where such exclusion or limitation is prohibited by applicable law.",
        },
        {
          title: "Intellectual Property",
          text: "The LE COIN VTC name, logo, platform design, software, text, graphics and other original platform content are protected by applicable intellectual-property laws.\nUsers remain responsible for content that they upload, including vehicle photographs, descriptions and business information.\nBy publishing content on LE COIN VTC, users grant LE COIN VTC a non-exclusive licence to host, reproduce, display and technically process that content to the extent necessary to operate and promote the marketplace.\nUsers must have the necessary rights to any content they upload.",
        },
        {
          title: "Personal Data and Privacy",
          text: "LE COIN VTC processes personal data in accordance with its Privacy Policy and applicable data-protection law.\nUsers should review the Privacy Policy for information about:\n- personal data collected;\n- purposes of processing;\n- legal bases;\n- data recipients;\n- retention periods;\n- cookies and tracking technologies; and\n- user data-protection rights.\nUsers must not misuse personal information obtained through the marketplace.",
        },
        {
          title: "Complaints and Disputes",
          text: "If a dispute arises between a Driver and a Rental Provider, the parties should first attempt to resolve the issue directly.\nLE COIN VTC may provide reasonable assistance or communication tools to help users resolve marketplace-related issues but is not required to decide contractual disputes between the parties unless expressly provided by a specific platform policy.\nComplaints regarding a LE COIN VTC account, listing, suspension, payment processed by the platform or platform service may be sent to:\nEmail: support@lecoinvtc.com\nUsers should include sufficient information to identify the account, booking or listing concerned.",
        },
        {
          title: "Changes to These Terms",
          text: "LE COIN VTC may update these Terms to reflect changes to the platform, pricing, features, legal requirements or business operations.\nWhere a material change affects professional users, LE COIN VTC will provide advance notice where required by applicable law.\nChanges required urgently for legal, regulatory, fraud-prevention, cybersecurity or platform-safety reasons may take effect sooner where permitted by law.\nContinued use of the platform after the effective date of updated Terms constitutes acceptance where legally permitted.",
        },
        {
          title: "Governing Law",
          text: "These Terms are governed by French law.\nAny dispute concerning these Terms or use of LE COIN VTC will be handled by the courts having jurisdiction under applicable French and European procedural rules, unless mandatory law provides otherwise.\nBefore starting formal proceedings, the parties are encouraged to seek an amicable resolution.",
        },
        {
          title: "Contact",
          text: "LE COIN VTC\nPublished and operated by ALVOS\nRegistered office: 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nSIREN: 928 450 063\nSIRET: 928 450 063 00013\nRCS: 928 450 063 R.C.S. Bobigny\nVAT number: FR51 928450063\nEmail: privacy@lecoinvtc.com",
        },
      ],
    },
  },

  contactPage: {
    title: "Contact",
    intro: "Have a question about your account, a vehicle listing, a booking, or using LE COIN VTC? Our support team is here to help.",
    response: "We aim to respond to enquiries within one business day.",
    emailTitle: "E-mail",
    email: "support@lecoinvtc.com",
    emailNote: "For account, booking, listing, verification, or general support enquiries.",
    phoneTitle: "Phone",
    // Official business phone, e.g. "+33 1 23 45 67 89"; empty = the card shows the support hours only
    phone: "",
    hoursTitle: "Support hours",
    hours: "Monday to Friday, 9:00–18:00",
    holidays: "Excluding French public holidays.",
    addressTitle: "Address",
    address: "LE COIN VTC\nPublished and operated by ALVOS\n1 Avenue Aristide Briand\n93160 Noisy-le-Grand\nFrance",
    emailCta: "Write to Us",
    faqText: "Many common questions are also answered in our",
    faqLink: "FAQ",
  },

  home: {
    heroTitle: "Find Your VTC Rental Car",
    heroText: "Compare hybrid and electric VTC rental cars from professional rental companies. Rent by the week or month, with no fees for drivers.",
    heroImageAlt: "VTC driver next to a blue electric rental car",
    heroPhoto: "photo · VTC",
    search: {
      location: "City or pickup location",
      start: "Start date",
      end: "End date",
      submit: "Search",
      submitAria: "Search for an available VTC car",
      endBeforeStart: "The end date must be after the start date.",
      pastDate: "Choose a date from today onwards.",
    },
    popularEmpty: "No cars available right now. Change your dates or see all offers.",
    popularError: "Vehicles couldn’t be loaded. Please refresh the page.",
    popularTitle: "VTC cars available",
    viewAllVehicles: "View all VTC cars",
    categoriesTitle: "VTC Rental Cars by Category",
    // sub: shown until the backend lists the makes found in the category
    categories: {
      electric: { label: "Electric", sub: "Tesla, Hyundai…" },
      hybrid: { label: "Hybrid", sub: "Toyota, Kia, Hyundai…" },
      premium: { label: "Premium Sedan", sub: "Mercedes E-Class…" },
      estate: { label: "Estate", sub: "Large boot, airport transfers" },
    },
    howTitle: "How to Rent a VTC Car",
    steps: [
      { title: "Search for Free", description: "Compare VTC vehicles, rental prices, availability, and rental conditions without creating an account." },
      { title: "Send Your Booking Request", description: "Create your free driver account, upload your documents, and request your preferred vehicle." },
      { title: "The Rental Company Confirms", description: "The rental company reviews your documents and approves your request. Payment is made directly to the company." },
    ],
    partner: {
      eyebrow: "For Professional VTC Rental Companies",
      title: "List Your VTC Cars for Free. 0% Commission.",
      listVehicles: "List my vehicles",
      seePricing: "View pricing",
      benefits: [
        { title: "Qualified Requests", description: "Receive booking requests from VTC drivers, including their profiles and professional documents." },
        { title: "Fleet Management", description: "Manage each vehicle's availability and prevent double bookings." },
        { title: "Unlimited Free Listings", description: "Take advantage of our launch offer: list unlimited vehicles with no subscription or commission." },
      ],
    },
  },

  companyPage: {
    breadcrumb: "Vehicles",
    ownerType: { company: "Rental company", individual: "Individual owner" },
    memberSince: (year: number) => `Member since ${year}`,
    reviewsCount: (n: number) => `${n} ${n === 1 ? "review" : "reviews"}`,
    noReviews: "No reviews yet",
    viewAll: (n: number) => `View all ${n} ${n === 1 ? "vehicle" : "vehicles"}`,
    about: "About",
    lockedText:
      "This company’s name, description and contact details are visible to registered drivers. Ratings, reviews and every vehicle are public.",
    unlock: "Unlock company information",
    stats: { listed: "Vehicles listed", available: "Available today", fleet: "Cars in the fleet" },
    infoTitle: "Company information",
    publicId: "Public ID",
    area: "Area",
    name: "Company name",
    address: "Address",
    contact: "Phone and email",
    locked: "Visible to registered drivers",
    memberSinceLabel: "Member since",
    vehiclesTitle: (label: string) => `Vehicles from ${label}`,
    vehiclesSub: (listed: number, available: number) =>
      `${listed} ${listed === 1 ? "vehicle" : "vehicles"} listed · ${available} available today`,
    all: "All",
    availableOnly: "Available only",
    sort: { default: "Sort: default", "price-asc": "Price: low to high", "price-desc": "Price: high to low" },
    noVehicles: "No vehicle matches this filter.",
    reviewsTitle: "Customer reviews",
    reviewsNote: "Reviews come from drivers who rented with this company and are checked by our team before publication.",
    rented: (vehicle: string) => `Rented ${vehicle}`,
    noReviewsText: "This company has no published reviews yet.",
    notFoundTitle: "Company not found",
    notFoundText: "This company doesn’t exist or isn’t listing vehicles at the moment.",
  },

  reviews: {
    title: "Reviews",
    ratingFromCompanies: "Your rating from companies",
    ratingFromDrivers: "Your rating from drivers",
    count: (n: number) => `${n} ${n === 1 ? "review" : "reviews"}`,
    toWriteTitle: "Waiting for your review",
    writtenTitle: "Reviews you posted",
    receivedTitle: "Reviews about you",
    noneWritten: "You haven’t posted any reviews yet.",
    noneReceived: "No reviews yet.",
    write: "Write a review",
    edit: "Edit",
    delete: "Delete",
    deleteConfirm: "Delete this review? This can’t be undone.",
    ratingLabel: "Rating",
    ratingRequired: "Choose a rating.",
    textRequired: "Write at least 10 characters.",
    placeholderDriver: "How was the vehicle, the handover, the deposit…?",
    placeholderCompany: "How did the rental go with this driver?",
    pendingNote: "Visible to others once approved by our team.",
    rejectedNote: "Not published by our team. You can edit it and send it again.",
    submit: "Post review",
    saveEdit: "Save and send for approval",
    sending: "Sending…",
    status: { pending: "Awaiting approval", published: "Published", rejected: "Not published" },
  },

  driverPages: {
    home: {
      vtcExpiring: (date: string) => `VTC professional card — expires on ${date}. Remember to renew it.`,
      vtcExpired: (date: string) => `VTC professional card — expired on ${date}. Upload your new card to keep booking.`,
      manageDocs: "Manage my documents",
      waiting: "Waiting for the company’s reply.",
      docsRequired: "The company needs updated documents. Check your messages.",
      confirmedText: (code: string) => (code ? `Confirmed — vehicle ${code} is reserved for you.` : "Confirmed — the company will assign your vehicle."),
      activeText: "Rental in progress.",
      viewDetails: "View details",
      activityLine: (status: string, vehicle: string) => `${status} · ${vehicle}`,
    },
    bookings: {
      title: "My bookings",
      tabs: { all: "All", requests: "Pending", upcoming: "Confirmed and active", past: "History" },
      empty: "No booking here yet.",
      find: "Find a vehicle",
      period: (start: string, end: string, days: number) => `${start} → ${end} · ${days} days`,
      unread: (n: number) => `${n} new ${n === 1 ? "message" : "messages"}`,
    },
    booking: {
      back: "My bookings",
      title: "Booking",
      sent: (date: string) => `Request sent on ${date}`,
      vehicle: "Vehicle",
      pickup: "Pick-up address",
      pickupHidden: "Shared once the company accepts your request.",
      company: "Company",
      period: "Period",
      rate: "Rates",
      estimate: "Estimated price",
      deposit: "Deposit",
      depositValue: (amount: string) => `${amount} · paid directly to the company`,
      unit: "Assigned vehicle",
      contactTitle: "Company contact",
      contactHidden: "The company’s phone, e-mail and address appear here once your request is accepted.",
      messagesTitle: "Messages",
      noMessages: "No messages yet. Ask the company a question about this booking.",
      placeholder: "Write to the company…",
      you: "You",
      platform: "Le Coin VTC",
      cancel: "Cancel my request",
      cancelConfirm: "Cancel this booking request? The company will be told.",
      cancelled: "Request cancelled",
      review: "Leave a review",
      declined: (reason: string) => (reason ? `Declined: ${reason}` : "The company declined this request."),
      notFound: "Booking not found.",
      history: "History",
    },
    messages: {
      title: "Messages",
      empty: "No conversations yet. Messages about your bookings appear here.",
      select: "Choose a conversation.",
      placeholder: "Write a message…",
    },
    favourites: {
      title: "Favourites",
      intro: "Vehicles you saved. Tap the heart on a vehicle page to add or remove one.",
      empty: "No favourites yet.",
      remove: "Remove",
    },
    profile: {
      title: "Profile",
      personal: "Personal details",
      firstName: "First name",
      lastName: "Last name",
      email: "E-mail",
      emailHint: "Your login e-mail. Changing it needs a code sent to the new address.",
      phone: "Phone",
      address: "Address",
      city: "City",
      postalCode: "Postcode",
      language: "E-mail language",
      professional: "Professional details",
      licenceNumber: "Driving licence number",
      licenceDate: "Licence obtained on",
      vtcCardNumber: "VTC card number",
      vtcCardExpiry: "VTC card expiry",
      save: "Save changes",
      saving: "Saving…",
      status: { pending: "Driver file under review", verified: "Verified driver", rejected: "Driver file rejected", suspended: "Account suspended" },
    },
    request: {
      sending: "Sending…",
      note: "Message to the company (optional)",
      notePlaceholder: "Tell the company about your activity (Uber, Bolt…), your experience, pick-up time…",
    },
  },

  card: {
    from: "From",
    monthlyAndDeposit: (monthly: string, deposit: string) => `${monthly}/month · Deposit: ${deposit}`,
    verifiedBy: (name: string) => `${name} · Verified rental company`,
    view: "View vehicle",
    sponsored: "SPONSORED",
  },

  vehicles: {
    area: "Area",
    startDate: "Start date",
    endDate: "End date",
    filters: (n: number) => (n ? `Filters · ${n}` : "Filters"),
    hideFilters: "Hide filters",
    // Heading of the results; the count shows beside it
    titleAll: "VTC Rental Cars in Île-de-France",
    titleCity: (city: string) => `VTC Rental Cars in ${city}`,
    dates: "Rental dates",
    sort: "Sort",
    sortOptions: {
      default: "Sort: relevance",
      "price-asc": "Price: low to high",
      "price-desc": "Price: high to low",
      available: "Most available",
    },
    noMatchTitle: "No Vehicles Match Your Search",
    noMatchText: "Adjust your filters, choose another city, or change your rental dates to discover more VTC cars available for rent.",
    resetFilters: "Reset filters",
    allAreas: "All of Île-de-France",
  },

  filters: {
    title: "Filters",
    reset: "Reset",
    availableOnly: "Available on my dates",
    maxWeekly: "Maximum weekly price",
    maxDeposit: "Maximum deposit",
    all: "All",
    minDuration: "Minimum rental duration",
    durations: {
      0: "Any duration",
      7: "7 days or less",
      14: "14 days or less",
      30: "30 days or less",
    },
    fuel: "Fuel Type",
    transmission: "Transmission",
    category: "Category",
    make: "Make",
  },

  vehicle: {
    prevPhoto: "Previous photo",
    nextPhoto: "Next photo",
    notFoundTitle: "Vehicle not found",
    notFoundText: "This listing may have been removed.",
    seeAll: "See all vehicles",
    allVehicles: "← All vehicles",
    photoViews: ["Front 3/4 view", "Interior", "Dashboard", "Rear", "Rear seats"],
    estimate: (days: number, basis: "day" | "week" | "month") =>
      `${days} ${plural(days, "day", "days")} · ${{ day: "daily rate", week: "weekly rate", month: "monthly rate" }[basis]}`,
    dateOrder: "Choose an end date after the start date.",
    dateMax: (n: number) => `Bookings are from 1 to ${n} days. Send a new request for a longer rental.`,
    driverOnly: "Log in with a driver account to request a booking.",
    specs: {
      year: "Year",
      fuel: "Fuel",
      gearbox: "Gearbox",
      seats: "Seats",
      colour: "Colour",
      mileage: "Mileage",
      category: "Category",
      fleet: "Vehicles in fleet",
    },
    terms: {
      daily: "Daily price",
      weekly: "Weekly rate",
      monthly: "Monthly rate",
      kmIncluded: "Mileage included",
      kmPerWeek: (km: string) => `${km} km / week`,
      extraKm: "Extra kilometre",
      deposit: "Deposit",
      depositValue: (amount: string) => `${amount} · paid to the company`,
    },
    specsTitle: "Specifications",
    termsTitle: "Rental terms",
    insurance: "Insurance",
    maintenance: "Maintenance",
    docsTitle: "Required documents",
    docsHint: "Uploaded once to your driver account and reused for future requests.",
    viewCompany: "View company profile",
    reviewsTitle: "Reviews of this company",
    allReviews: (n: number) => `See all ${n} reviews`,
    memberSince: (since: number) => `member since ${since}`,
    companyMeta: (since: number, rating: number, reviews: number, hours: number) =>
      `member since ${since} · ${rating}/5 (${reviews} reviews) · replies in under ${hours} h`,
    contactHint: "The company’s contact details are shared with you once your request is accepted.",
    depositDirect: (amount: string) => `Deposit ${amount} · paid directly to the company`,
    start: "Start",
    end: "End",
    availability: (n: number, total: number) =>
      `${n} ${plural(n, "vehicle", "vehicles")} available out of ${total} for these dates`,
    requestSent: (name: string) => `Request sent to ${name}.`,
    notified: "You’ll be notified when they reply.",
    requestBooking: "Request a booking",
    savedFavourite: "Saved to favourites",
    addFavourite: "Add to favourites",
    disclaimer:
      "No payment on the platform. Your request is subject to the company’s approval; displayed availability is indicative.",
    similar: "Similar vehicles",
  },

  howPage: {
    title: "How Does LE COIN VTC Work?",
    intro: "LE COIN VTC is a marketplace specializing in VTC car rentals. Drivers find vehicles suited to their professional needs, while rental companies list their cars and manage rental requests.",
    driversTitle: "VTC Drivers · 100% Free",
    companiesTitle: "Professional Rental Companies · Free During Launch",
    findVehicle: "Find a VTC Car",
    companyArea: "Rental Company Dashboard",
    faqTitle: "Frequently Asked Questions",
    driverSteps: [
      { title: "Search Without Registering", text: "Find a VTC rental car by city, fuel type, weekly or monthly price, and availability." },
      { title: "Request a Booking", text: "Create your free driver account to submit a booking request. Your selected vehicle and rental dates are saved." },
      { title: "Upload Your Documents Once", text: "Upload your ID, driving licence, professional VTC card, and proof of address. Reuse them for future booking requests." },
      { title: "Receive the Rental Company's Response", text: "Track approvals, rejections, or requests for additional information from your driver dashboard. Pay the rental company directly." },
    ],
    companySteps: [
      { title: "Create Your Rental Company Account", text: "Register for free and provide your business details. Upload the required documents to verify your account." },
      { title: "Enjoy Free Launch Access", text: "Publish unlimited active vehicle listings with no subscription fees and 0% commission on your rentals." },
      { title: "List Your VTC Vehicles", text: "Create listings with photos, prices, deposits, rental terms, and availability. Manage multiple vehicles from your company dashboard." },
      { title: "Manage Booking Requests", text: "Review driver profiles and documents, approve booking requests, and assign available vehicles without double bookings." },
    ],
    faq: [
      { q: "Is LE COIN VTC free for drivers?", a: "Yes. Creating a driver account, searching for vehicles, and submitting booking requests are completely free. LE COIN VTC does not charge drivers any booking fees." },
      { q: "How are rental payments and deposits handled?", a: "Rental payments and security deposits are paid directly to the rental company according to its rental terms. LE COIN VTC does not collect these payments." },
      { q: "Is my booking confirmed immediately?", a: "No. You submit a booking request, which the rental company reviews. The company can accept, reject, or request additional information before confirming the rental." },
      { q: "What documents do I need to rent a VTC car?", a: "You generally need a valid ID, driving licence, professional VTC card, and recent proof of address. Rental companies may request additional documents." },
    ],
  },

  forCompanies: {
    eyebrow: "For Professional VTC Rental Companies",
    title: "Rent Your VTC Cars to Professional Drivers",
    subtitle: "List your vehicles for free, receive booking requests from VTC drivers, and grow your car rental business. Enjoy 0% commission on your rentals.",
    start: "Start for free",
    pricing: "View pricing",
    features: [
      { title: "Qualified Driver Requests", text: "Review VTC driver profiles and document verification status before accepting a booking request." },
      { title: "One Listing, Multiple Vehicles", text: "List a vehicle model once and manage multiple physical vehicles, including registrations, maintenance, and availability." },
      { title: "No Double Bookings", text: "Each vehicle has its own availability calendar to prevent overlapping reservations." },
      { title: "Free Listings, Zero Commission", text: "During our launch period, publish unlimited listings with no subscription fees. Collect rental payments directly and keep 100% of your rental revenue." },
    ],
    notTitle: "What LE COIN VTC Does Not Do",
    notText: "LE COIN VTC connects rental companies with professional VTC drivers. Rental payments and security deposits are handled directly between both parties. The platform does not charge any commission.",
    areas: "Areas drivers search in",
  },

  auth: {
    roles: {
      driver: {
        tab: "Driver",
        loginTitle: "Log in",
        loginSubtitle: "Find and book a VTC vehicle in a few clicks.",
        signupTitle: "Create my driver account",
        signupSubtitle: "Free, no subscription or booking fees.",
        signupLabel: "Create a driver account",
        resetSubtitle:
          "Enter the e-mail linked to your driver account and we'll send you a reset link.",
      },
      company: {
        tab: "Company",
        loginTitle: "Company area",
        loginSubtitle: "Log in with a company account to access your fleet.",
        signupTitle: "Create a account",
        signupSubtitle:
          "For rental companies and individuals. Free plan up to 3 listings, 0% commission. Choose Individual or Company below; verification documents are added from your account.",
        signupLabel: "Create a company account",
        resetSubtitle:
          "Enter the e-mail linked to your company account and we'll send you a reset link.",
      },
    },
    accountType: "Account type",
    invalidCredentials: "Incorrect email or password.",
    toasts: {
      loggedIn: (name: string) => `Welcome back, ${name}!`,
      signedUp: "Your account has been created.",
      companySignedUp: "Account created. Add your verification documents from your account to start publishing.",
      loggedOut: "You have been logged out.",
    },
    enterEmail: "Enter your email.",
    rules: {
      emailRequired: "Enter your email.",
      emailInvalid: "Enter a valid email address.",
      phoneRequired: "Enter your phone number.",
      phoneInvalid: "Enter a valid phone number.",
      passwordRequired: "Choose a password.",
      passwordMin: "Use at least 8 characters.",
      passwordPattern: "Use at least one letter and one number.",
    },
    login: {
      passwordRequired: "Enter your password.",
      passwordMin: "Password must be at least 6 characters.",
      remember: "Remember me",
      forgot: "Forgot password?",
      submitting: "Logging in…",
      noAccount: "No account yet?",
    },
    signup: {
      haveAccount: "Already have an account?",
    },
    forgot: {
      title: "Reset your password",
      checkInbox: "Check your inbox.",
      sent: (email: string) =>
        `If an account exists for ${email}, you'll receive a reset link in a few minutes. Check your spam folder too.`,
      differentEmail: "Use a different email",
      sending: "Sending…",
      submit: "Send reset link",
    },
    reset: {
      title: "Choose a new password",
      subtitle: "Use at least 8 characters, with a letter and a number.",
      newPassword: "New password",
      submit: "Save new password",
      submitting: "Saving…",
      doneTitle: "Password changed.",
      doneText: "You've been signed out everywhere. Log in with your new password.",
      missingTitle: "This link is incomplete.",
      missingText: "Open the link from the e-mail again, or ask for a new one.",
      requestNew: "Get a new reset link",
    },
    terms: {
      before: "I accept the ",
      termsLink: "terms of use",
      middle: " and the ",
      privacyLink: "privacy policy",
      after: ".",
      required: "You must accept the terms to continue.",
    },
    driverSignup: {
      firstName: "First name",
      lastName: "Last name",
      firstNameRequired: "Enter your first name.",
      lastNameRequired: "Enter your last name.",
      vtcCard: "VTC card number",
      vtcCardPlaceholder: "e.g. 075-2024-001234",
      vtcCardRequired: "Enter your VTC card number.",
      confirmPassword: "Confirm password",
      confirmRequired: "Confirm your password.",
      mismatch: "Passwords don't match.",
      submitting: "Creating account…",
      submit: "Create account",
      steps: ["Account", "Identity", "Licence and VTC"],
      licenceDate: "Licence issue date",
      licenceDateRequired: "Enter the date you got your licence.",
      licenceDateFuture: "This date is in the future.",
      emailPlaceholder: "prenom.nom@exemple.fr",
      mobile: "Mobile",
      mobileInvalid: "Invalid mobile number",
      addressPlaceholder: "Number and street",
      postcode: "Postcode",
      licenceNumber: "Driving licence number",
      vtcCardNumberPlaceholder: "VTC-075-2024-0000",
      vtcCardExpiry: "VTC card expiry date",
      vtcCardExpired: "Your VTC card has expired",
      continue: "Continue",
      createAccount: "Create my account",
      creating: "Creating your account…",
      codeSentTo: (email: string) => `Verification code sent to ${email}`,
      verifyTitle: "Verify your email",
      verifyBefore: "We sent a 6-digit code to ",
      verifyAfter: ". Enter it to activate your account.",
      codeLabel: "6-digit code",
      codeRequired: "Enter the 6-digit code from the e-mail.",
      resend: "Resend code",
      resending: "Sending…",
      verify: "Verify email",
      verifying: "Verifying…",
      nextNote:
        "Next: upload your ID, driving licence, VTC card and proof of address from your driver account. They're required before your first booking request.",
      missingEmail: "Open this page from the sign-up or log-in form to verify your e-mail.",
    },
    companySignup: {
      codeNextNote:
        "Next: add your verification documents from your account. Our team checks them before your listings go live.",
      steps: ["Contact", "Company", "Documents"],
      stepOf: (current: number, total: number) => `Step ${current} of ${total}`,
      emailAddress: "Email address",
      phoneNumber: "Phone number",
      passwordPlaceholder: "At least 8 characters",
      ownerType: "Account type",
      ownerTypeRequired: "Choose an account type",
      ownerTypes: {
        individual: {
          label: "Individual",
          desc: "You rent out your own vehicle(s) as a private person. No company documents needed.",
        },
        company: {
          label: "Company",
          desc: "A registered business (SIRET) renting out vehicles. Company documents are verified.",
        },
      },
      individualNote: "You can convert to a Company account later from your account settings.",
      companyNamePlaceholder: "Registered company name",
      companyNameRequired: "Required for a company account",
      businessType: "Business type",
      businessTypeRequired: "Choose your business type",
      businessTypeDescs: {
        "vtc-rental":
          "Companies specialising in renting vehicles to VTC drivers (Uber, Bolt and other private-hire drivers).",
        "individual-rental": "Private individuals renting out their own vehicles.",
        "rental-company": "Traditional car rental businesses renting vehicles to individuals or businesses.",
      },
      createAccount: "Create my account",
      creating: "Creating your account…",
      selectPlaceholder: "Select…",
      businessTypes: {
        "vtc-rental": "VTC Car Rental",
        "individual-rental": "Individual Car Rental",
        "rental-company": "Regular Car Rental Company",
      },
      businessEmail: "Business email",
      emailInvalid: "Invalid email address",
      phoneInvalid: "Invalid number",
      passwordMin: "At least 8 characters",
      companyName: "Company name",
      siretPlaceholder: "14 digits",
      siretInvalid: "SIRET must have 14 digits",
      vat: "VAT no. (optional)",
      vatInvalid: "Invalid VAT number",
      address: "Address",
      addressPlaceholder: "Start typing your address",
      city: "City",
      postalCode: "Postal code",
      state: "State / Region",
      country: "Country",
      fileTooLarge: "File too large (10 MB max)",
      fileType: "PDF, JPG or PNG only",
      docRequired: "Required document",
      docsHint: "PDF, JPG or PNG, 10 MB max per file.",
      continue: "Continue",
      submitting: "Submitting…",
      submit: "Submit for verification",
      legalBefore: "By submitting, you accept the ",
      legalAfter: ". Phone and email are only shared with drivers after a request is accepted.",
    },
    documents: {
      kbis: { label: "Kbis extract", hint: "Under 3 months" },
      insurance: { label: "Fleet insurance certificate", hint: "Covering VTC rental activity" },
      licence: { label: "VTC register entry / licence", hint: "Proof of VTC rental activity" },
      vehicleRegistration: { label: "Vehicle registration certificate (carte grise)", hint: "Carte grise in your name" },
      vehicleInsurance: { label: "Vehicle insurance certificate", hint: "Must cover rental to third parties" },
      vtcCard: { label: "VTC professional card", hint: "Currently valid" },
      drivingLicence: { label: "Driving licence", hint: "Front and back" },
      idDocument: { label: "ID document", hint: "ID card or passport" },
      proofOfAddress: { label: "Proof of address", hint: "Under 3 months" },
      upload: "Upload",
      replace: "Replace",
      remove: (label: string) => `Remove ${label}`,
    },
  },

  driverArea: {
    area: "Driver area",
    findVehicle: "Find a vehicle",
    nav: {
      aria: "Driver area",
      dashboard: "Dashboard",
      bookings: "My bookings",
      documents: "My documents",
      messages: "Messages",
      favourites: "Favourites",
      reviews: "Reviews",
      profile: "Profile",
    },
    home: {
      hello: (name: string) => `Hello ${name}`,
      intro: "Track your booking requests and keep your documents up to date.",
      completeTitle: "Complete your driver file",
      completeText: (n: number) =>
        `Upload your ${n} documents once. Companies need them to accept your booking requests.`,
      uploaded: (done: number, total: number) => `${done} / ${total} documents uploaded`,
      uploadDocs: "Upload documents",
      sentTitle: "Driver file sent",
      sentText: "Our team is checking your documents. You can send booking requests once they're approved.",
      trackTitle: "Booking to track",
      noRequest: "No request in progress.",
      findVtc: "Find a VTC vehicle",
      activity: "Recent activity",
      noActivity: "Nothing yet.",
      uploadedOn: (doc: string, date: string) => `${doc} uploaded on ${date}`,
      stats: {
        pending: "Pending requests",
        confirmed: "Confirmed or active",
        approvedDocs: "Approved documents",
        favourites: "Favourites",
      },
    },
    documents: {
      title: "My documents",
      intro:
        "Uploaded once, reused for every request. Your documents are only visible to the companies you send a request to and to the moderation team.",
      notProvided: "Not provided. Required to send a request.",
      sentOn: (file: string, date: string) => `${file} · sent on ${date}`,
      approved: (file: string) => `${file} · approved`,
      approvedUntil: (file: string, date: string) => `${file} · approved · expires on ${date}`,
      expiringSoon: "Expires soon",
      expired: "Expired",
      renewHint: "Upload your renewed document before it expires: companies can't accept requests with an expired document.",
      wrongDate: "Wrong expiry date? Correct it in your profile",
      rejected: (note: string) => `Rejected: ${note}`,
      rejectedNoNote: "Rejected. Upload a new document.",
      upload: "Upload",
      replace: "Replace",
      uploading: "Uploading…",
      view: "View",
    },
  },

  company: {
    header: {
      addVehicle: "Add a vehicle",
    },
    nav: {
      aria: "Company area",
      overview: "Overview",
      listings: "My listings",
      bookings: "Bookings",
      calendar: "Calendar",
      customers: "Customers",
      messages: "Messages",
      reviews: "Reviews",
      subscription: "Subscription",
      profile: "Company profile",
      accountProfile: "Account profile",
    },
    launch: {
      name: "Free launch access",
      usage: (n: number, limit: number | null) =>
        limit ? `${n} / ${limit} active listings · Free launch access` : `${n} active listings · Free launch access`,
    },
    mySubscription: {
      title: "My subscription",
      loading: "Loading your subscription…",
      active: "Active",
      launchTitle: "Free launch access",
      launchPrice: "€0/month · no card required",
      activeListings: "Active listings",
      usage: (n: number, limit: number | null) => (limit ? `${n} / ${limit}` : `${n} · unlimited`),
      launchText: (days: number) =>
        `Paid plans will be introduced later. You’ll be notified at least ${days} days before any change, and your listings are never deleted.`,
      sponsorBadge: "Sponsored",
      sponsorTitle: "Optional add-on",
      sponsorText: (options: string) =>
        `${options}. Sponsored listings appear at the top of search results. No automatic renewal by default.`,
      sponsorCta: "Sponsor a listing",
      footnote: "0% commission on your rentals. Rent and deposits are paid directly by drivers.",
      plansTitle: "Plans",
      plansText: "Paid plans are being set up. Contact us to choose a plan; your listings stay online in the meantime.",
      perMonth: "/month excl. VAT",
      listingLimit: (n: number) => `${n} active listings`,
      contact: "Contact us",
    },
    sponsor: {
      badge: "Sponsored",
      until: (date: string) => `Until ${date}`,
      sponsor: "Sponsor",
      extend: "Extend sponsorship",
      makeTitle: "Sponsor this listing",
      extendTitle: "Extend sponsorship",
      text: "Sponsored listings show a Sponsored badge and appear at the top of search results.",
      option: (days: number, price: string) => `${days} days — ${price}`,
      untilDate: (date: string) => `until ${date}`,
      activeUntil: (date: string) => `Sponsored until ${date}`,
      ok: "Sponsor listing",
      extendOk: "Extend",
      note: "One-off payment, prices excl. VAT. No automatic renewal.",
      unavailable: "Sponsored listings are not available at the moment.",
      redirecting: "Opening secure payment…",
      pay: (amount: string) => `Pay ${amount}`,
      vatLine: (ht: string, vat: string, ttc: string) => `${ht} excl. VAT + ${vat} VAT (20%) = ${ttc}. Secure payment by Stripe.`,
      paid: (date: string) => `Payment received — sponsored until ${date}`,
      cancelled: "Payment cancelled. Your listing was not sponsored.",
    },
    sidebar: {
      usage: (n: number, limit: number, plan: string) => `${n} / ${limit} active listings · Plan ${plan}`,
      noPlan: "No active plan",
    },
    banners: {
      incomplete: {
        title: "Complete your account to get verified.",
        text: "Add your company information and upload your documents. You can prepare draft listings in the meantime.",
        cta: "Complete verification",
      },
      pending: {
        title: "Verification in progress.",
        text: "You can prepare draft listings; publishing will be possible once your file (SIRET, Kbis, insurance) is approved.",
      },
      rejected: {
        title: "Verification rejected.",
        text: "Update your documents to resubmit your file.",
        cta: "Update my documents",
      },
      noPlan: {
        title: "Company verified.",
        text: "Choose a plan to start your 30-day free trial and publish your vehicles.",
        cta: "Choose a plan",
      },
      pastDue: {
        title: "Payment overdue.",
        text: (date: string) =>
          `The payment on ${date} failed. Your listings stay online but publishing new vehicles is blocked.`,
        cta: "Update payment method",
      },
      trial: {
        title: (days: number) => `Free trial — ${days} days left.`,
        text: (date: string) => `Ends on ${date}. Add a payment method to continue without interruption.`,
        cta: "Add a payment method",
      },
      inactive: {
        title: { cancelled: "Subscription cancelled.", expired: "Subscription expired." },
        text: "Your listings and data are kept, but you cannot publish new vehicles.",
        cta: "Reactivate",
      },
    },
    overview: {
      title: "Overview",
      kpis: {
        activeListings: "Active listings",
        available: "Available",
        activeRentals: "Active rentals",
        newRequests: "New requests",
        listingLimit: "Listing limit",
        noLimit: "No limit",
      },
      actions: {
        addVehicle: "Add a vehicle",
        viewRequests: "View requests",
        manageSubscription: "Manage my subscription",
      },
      queue: {
        title: "Booking requests to handle",
        received: (date: string) => `received ${date}`,
        empty: "No pending requests.",
      },
      movements: {
        title: "Upcoming pick-ups and returns",
        pickup: "Pick-up",
        return: "Return",
        toAssign: "to assign",
        empty: "Nothing scheduled in the next 14 days.",
      },
    },
    docSummary: {
      blocking: (n: number) => `${n} missing or expired`,
      review: (n: number) => `${n} under review`,
      approved: (n: number) => `${n}/${n} approved`,
    },
    visibility: {
      visible: "Visible on the marketplace",
      hidden: (reason: string) => `Not visible · ${reason}`,
      reasons: {
        draft: "draft",
        paused: "paused",
        moderated: "under moderation",
        archived: "archived",
        notVerified: "company not verified",
        noPlan: "no active plan",
      },
    },
    errors: {
      mustBeVerified: "your company must be verified before publishing.",
      choosePlan: "choose a plan before publishing.",
      limit: (plan: string, n: number) => `Your ${plan} plan includes ${n} active listings.`,
      codeRequired: "Enter an internal code.",
      codeExists: (code: string) => `${code} already exists in your fleet.`,
      invalidPeriod: "Invalid period.",
      overlap: (ref: string) => `A confirmed booking (${ref}) overlaps these dates.`,
      notFree: (code: string) => `${code} is not free on these dates.`,
      assignFirst: "Assign a vehicle first.",
      blocked: "Blocked",
      urgentInvoice: (days: number) => `Urgent ${days} days`,
    },
    publishFlow: {
      savedAsDraft: (reason: string) => `Saved as draft — ${reason}`,
      published: "Listing published",
    },
    listings: {
      title: "My listings",
      availableToday: "available today",
      urgentActive: "URGENT ACTIVE",
      expiresOn: (date: string) => `Expires on ${date}`,
      makeUrgent: "Make Urgent",
      extend: "Extend",
      pause: "Pause",
      paused: "Listing paused",
      archive: "Archive",
      archiveTitle: "Archive this listing?",
      archiveText: "It will be removed from the marketplace. Booking history is kept.",
      archived: "Listing archived",
      emptyTitle: "No vehicles yet",
      emptyText: "Create one listing per model, then add your physical vehicles.",
      notFound: "This listing doesn't exist.",
      loading: "Loading your listings…",
      delete: "Delete",
      deleteTitle: "Delete this draft?",
      deleteText: "The listing, its photos and its physical vehicles will be removed for good.",
    },
    editor: {
      tabs: {
        info: "Details",
        photos: "Photos",
        terms: "Rates and terms",
        docs: "Documents required",
        units: "Physical vehicles",
        preview: "Preview",
      },
      editTitle: (name: string) => `Edit · ${name}`,
      addTitle: "Add a vehicle",
      amountRequired: "Amount required",
      unitsRequired: "Add at least one physical vehicle.",
      saved: "Changes saved",
      published: "Vehicle published",
      fields: {
        make: "Make",
        model: "Model",
        version: "Version",
        year: "Year",
        fuel: "Fuel",
        gearbox: "Gearbox",
        category: "Category",
        pickup: "Pick-up location",
        seats: "Seats",
        colour: "Colour",
        mileage: "Average mileage (km)",
        description: "Description",
        daily: "Daily price (€)",
        weekly: "Weekly rate (€)",
        monthly: "Monthly rate (€)",
        deposit: "Deposit (€)",
        minDays: "Minimum duration (days)",
        kmIncluded: "Km included per week",
        extraKm: "Price per extra km (€)",
        insurance: "Insurance",
        maintenance: "Maintenance",
      },
      address: {
        title: "Pick-up address",
        hint: "Drivers only see the city publicly. The full address is shared with the driver once you accept their request.",
        search: "Search an address",
        searchPlaceholder: "Start typing, e.g. 15 Avenue de la République",
        street: "Street number and street name",
        postalCode: "Postal code",
        city: "City",
        country: "Country",
        saved: "Saved address",
        publicCity: "Public search city",
      },
      photosHint: "Add up to 5 photos. The first is used as the main photo.",
      mainPhoto: "Main photo ✓",
      photoN: (n: number) => `Photo ${n} ✓`,
      addPhoto: "+ Add a photo",
      photoTooLarge: "Each photo must be 10 MB max.",
      weeklyAuto: "Calculated from the daily price (× 7). You can change it, e.g. to offer a weekly discount.",
      monthlyAuto: "Calculated from the weekly rate (× 52 ÷ 12). You can change it, e.g. to offer a monthly discount.",
      photoType: (name: string) => `"${name}" isn't a supported image. Use a JPG, PNG or WebP photo.`,
      next: "Next",
      saving: "Saving…",
      docsHint: "Documents the driver must provide before sending a request.",
      unitsHint:
        "A public listing can group several identical vehicles. The availability shown to drivers is calculated from these physical vehicles.",
      codePlaceholder: "Internal code (e.g. TM3-008)",
      code: "Internal code",
      registration: "Registration",
      addUnit: "+ Add a physical vehicle",
      previewHint: "Preview of the listing as it will appear on the marketplace.",
      from: "From",
      fleetCount: (n: number) => `${n} ${plural(n, "vehicle", "vehicles")} in fleet`,
      termsHint: "Requests already sent keep the terms in force when they were sent.",
      saveChanges: "Save changes",
      saveDraft: "Save draft",
      publishVehicle: "Publish vehicle",
    },
    listingDetail: {
      viewPublic: "View public listing",
      pickup: "Pick-up address",
      stats: {
        units: "Physical vehicles",
        availableToday: "Available today",
        availableRange: (from: string, to: string) => `Available ${from} → ${to}`,
        inMaintenance: "In maintenance",
      },
      unitsTitle: "Physical vehicles",
      unitsHint: "A vehicle cannot be assigned to two overlapping bookings.",
      cols: {
        code: "Code",
        registration: "Registration",
        mileage: "Mileage",
        status: "Status",
        occupation: "Occupation",
      },
      rented: (id: string, end: string) => `Rented · ${id} until ${end}`,
      booked: (start: string, end: string, id: string) => `Booked ${start} → ${end} · ${id}`,
      blocked: (start: string, end: string, reason: string) => `Blocked ${start} → ${end} · ${reason}`,
      free: "Free",
      added: (code: string) => `${code} added to fleet`,
      backInService: (code: string) => `${code} back in service`,
      nowInMaintenance: (code: string) => `${code} in maintenance`,
      returnToService: "Return to service",
      maintenance: "Maintenance",
      blockDates: "Block dates",
      codePlaceholder: (code: string) => `Code (e.g. ${code})`,
      addUnit: "Add a physical vehicle",
      bookingsTitle: "Bookings for this listing",
      notAssigned: "Not assigned",
      noBookings: "No bookings yet.",
    },
    bookings: {
      title: "Booking requests",
      tabs: {
        new: "New",
        documents: "Documents required",
        confirmed: "Confirmed",
        active: "Active",
        history: "History",
      },
      cols: {
        reference: "Reference",
        driver: "Driver",
        vehicle: "Vehicle",
        dates: "Dates",
        documents: "Documents",
        status: "Status",
      },
      empty: "No requests in this category.",
    },
    booking: {
      notFound: "This booking doesn't exist.",
      title: "Request",
      received: (date: string) => `Received ${date}`,
      requestCard: "Request",
      vehicle: "Vehicle",
      pickup: "Pick-up address",
      period: "Period",
      periodValue: (from: string, to: string, days: number) => `${from} → ${to} (${days} days)`,
      rate: "Rate (frozen)",
      estimate: "Estimated price",
      deposit: "Deposit",
      kmIncluded: "Mileage included",
      kmValue: (km: number) => `${km} km / week`,
      availableForPeriod: "Available for the period",
      driverMessage: "Driver’s message",
      driverMeta: (city: string, joined: string, year: number) =>
        `${city} · joined ${joined} · licensed since ${year}`,
      contactHidden: "Contact details visible after the request is accepted.",
      driverDocs: "Driver’s documents",
      validUntil: (date: string) => `Valid until ${date}`,
      notProvided: "Not provided",
      docsHint: "Access limited to companies involved in a request. Every view is logged.",
      messages: "Messages",
      noMessages: "No messages.",
      writeToDriver: "Write to the driver…",
      actions: "Actions",
      assigned: (code: string, plate: string) => `Assigned vehicle: ${code} · ${plate}`,
      deferred: "Deferred assignment — no physical vehicle assigned yet.",
      waiting: "Waiting for the driver’s reply.",
      accept: "Accept",
      requestInfo: "Request information",
      decline: "Decline",
      neverAuto: "A request is never confirmed automatically.",
      changeVehicle: "Change vehicle",
      assignVehicle: "Assign a vehicle",
      handover: "Vehicle handed over to driver",
      rentalStarted: "Rental started",
      closeRental: "Close rental",
      rentalClosed: "Rental closed",
      noActions: "No actions available for this status.",
      history: "History",
      byDriver: "Driver",
      you: "You",
      platform: "Platform",
      docPreview: (file: string) => `Confidential preview · ${file}`,
    },
    vehicleModal: {
      acceptTitle: (id: string) => `Accept request ${id}`,
      assignTitle: "Assign a vehicle",
      noneFree:
        "No physical vehicle is free on these dates. You can accept with deferred assignment or decline the request.",
      choose: (from: string, to: string) =>
        `Choose the physical vehicle for the period from ${from} to ${to}. Vehicles already booked on overlapping dates are blocked.`,
      acceptOk: "Accept and confirm",
      assignOk: "Assign",
      chooseOrDefer: "Choose a vehicle or deferred assignment.",
      chooseAvailable: "Choose an available vehicle.",
      accepted: "Request accepted — booking confirmed",
      assigned: "Vehicle assigned",
      inMaintenance: "In maintenance",
      alreadyBooked: (from: string, to: string, ref: string) => `Already booked ${from} → ${to} (${ref})`,
      blocked: (reason: string) => `Blocked: ${reason}`,
      available: (plate: string) => `Available · ${plate}`,
      later: "Assign the vehicle later",
      messagePlaceholder: "Message to driver (optional): pick-up place and time…",
    },
    messageModal: {
      declineTitle: "Decline request",
      infoTitle: "Request information",
      declineText: "Give the reason for declining. It will be sent to the driver.",
      infoText:
        "Specify the documents or information needed. The request will move to “Documents required”.",
      declineOk: "Decline request",
      infoOk: "Send request",
      required: "A message is required.",
      declined: "Request declined",
      infoSent: "Information request sent",
      declinePlaceholder: "Reason for declining…",
      infoPlaceholder: "E.g. please send a valid VTC card.",
    },
    calendar: {
      title: "Availability calendar",
      previous: "Previous month",
      next: "Next month",
      months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      legend: {
        confirmed: "Confirmed",
        active: "Rental in progress",
        blocked: "Blocked",
        maintenance: "Maintenance",
      },
      hint: "Click a vehicle to block dates.",
      vehicle: "Vehicle",
      empty: "No physical vehicles yet.",
      unassigned: (n: number) =>
        `${n} pending ${plural(n, "request is", "requests are")} not yet assigned to a vehicle and ${plural(n, "does", "do")} not appear in the calendar.`,
    },
    customers: {
      title: "Customers",
      contactHidden: "contact details after acceptance",
      bookings: (n: number) => `${n} ${plural(n, "booking", "bookings")}`,
      latest: "Latest request",
      empty: "No customers yet. Drivers appear here after their first request.",
    },
    messages: {
      title: "Messages",
      empty: "No conversations.",
      select: "Select a conversation",
      placeholder: "Write a message…",
    },
    subscription: {
      title: "My subscription",
      currentPlan: "Current plan",
      planName: (name: string) => `Plan ${name}`,
      perMonthExclVat: "/month excl. VAT",
      activeListings: "active listings",
      limitReached: "Limit reached",
      remaining: (n: number) => `${n} remaining`,
      trialEnds: "Trial ends",
      nextBilling: "Next billing",
      paymentMethod: "Payment method",
      none: "None",
      commission: "Commission on rentals",
      updatePayment: "Update payment method",
      downloadInvoices: "Download my invoices",
      downloading: "Downloading invoices (demo)",
      cancel: "Cancel my subscription",
      reactivate: "Reactivate my subscription",
      reactivated: "Subscription reactivated",
      invoices: "Invoices",
      inclVat: (amount: string) => `${amount} incl. VAT`,
      noInvoices: "No invoices during the free trial.",
      notVerified: "You can choose a plan once our team has verified your company.",
      changePlan: "Change plan",
      choosePlan: "Choose a plan",
      currentBadge: "Current plan",
      listingCount: (n: number) => `${n} active listings`,
      startTrial: "Start free trial",
      startFree: "Start for free",
      switchTo: (name: string) => `Switch to ${name}`,
      trialActivated: (name: string) => `Free trial ${name} activated — 30 days`,
      freeActivated: "Free plan activated",
      planActivated: (name: string) => `Plan ${name} activated`,
      urgentTitle: "Add-on option, on top of your plan",
      urgentText: (week: string, month: string) =>
        `7 days — ${week} · 30 days — ${month}. Listing featured at the top of results. No automatic renewal by default.`,
      makeUrgent: "Make a listing Urgent",
      footer:
        "Fixed subscription. 0% commission on your rentals. Rent and deposits are paid directly by drivers. Changing plan never deletes your listings.",
      paymentTitle: "Update payment method",
      saveCard: "Save card",
      cardNumber: "Card number",
      expiry: "Expiry",
      paymentHint: "Subscription payment only — simulation, no payment provider connected.",
      paymentUpdated: "Payment method updated",
      cancelTitle: "Cancel my subscription?",
      cancelText:
        "Your listings and history are kept, but you won’t be able to publish new vehicles. You can reactivate at any time.",
      keep: "Keep my subscription",
      cancelled: "Subscription cancelled — your listings are kept",
      downgradeTitle: (name: string) => `Switch to ${name}?`,
      downgradeText: (usage: number, limit: number) =>
        `You have ${usage} active listings for a limit of ${limit}. No listing will be deleted, but you won’t be able to publish new vehicles while you exceed the limit.`,
      confirmChange: "Confirm change",
      planChanged: "Plan changed",
    },
    profile: {
      title: "Company profile",
      fields: {
        name: "Company name",
        siret: "SIRET",
        vat: "VAT no.",
        address: "Address",
        city: "City",
        postalCode: "Postal code",
        region: "Region",
        country: "Country",
        firstName: "First name",
        lastName: "Last name",
        phone: "Phone",
        email: "E-mail",
      },
      saved: "Profile saved",
      update: "Update profile",
      updating: "Updating…",
      hint: "Phone and email are only shared with drivers after a request is accepted.",
      verification: "Verification",
      users: "Users",
      usersText: (who: string) =>
        `${who} (administrator). Team accounts: coming soon — up to 3 with Pro, 8 with Business, 20 with Fleet.`,
      accountInfo: "Account information",
      createdOn: (date: string) => `Details provided when the account was created on ${date}.`,
      accountType: "Account type",
      businessType: "Business type",
      publicProfile: "Public profile and address",
      companyDetails: "Company details",
      displayName: "Display name",
      vatOptional: "VAT no. (optional)",
      requiredForVerification: (fields: string) => `Required for verification: ${fields}`,
      save: "Save",
      saving: "Saving…",
      verificationSubtitle: (owner: string, business: string) => `${owner} · ${business}`,
      upload: "Upload",
      replace: "Replace",
      uploading: "Uploading…",
      uploadThenSubmit: "Upload every document, then submit.",
      submitForVerification: "Submit for verification",
      submitting: "Submitting…",
      submittedOn: (date: string) => `Submitted on ${date}. Our team usually replies within 1 business day.`,
      verifiedText: "Your account is verified. You can publish your vehicles.",
      rejectedText: "Your file was rejected. Replace the documents concerned, then submit again.",
      reviewNote: (note: string) => `Reviewer's note: ${note}`,
      convertTitle: "Renting as a business?",
      convertText: "Convert to a Company account to rent under your company name with SIRET and company verification.",
      convertButton: "Convert to company account",
    },
    convert: {
      back: "Company profile",
      title: "Convert to a company account",
      intro:
        "Your listings and bookings are kept. Your account becomes a Company account straight away; our team then verifies your company before you can publish new vehicles.",
      steps: [
        { title: "Company information", text: "Company name, SIRET, VAT number and head office address." },
        { title: "Business type", text: "VTC Car Rental or Regular Car Rental Company." },
        { title: "Verification documents", text: "Kbis, insurance and other documents required for your business type." },
        { title: "Review", text: "Live listings stay online while your company is verified." },
      ],
      start: "Start conversion",
      companyInfo: "Company information",
      siretPlaceholder: "912 345 678 00021",
      vatPlaceholder: "FR00 000000000",
      headOffice: "Head office address",
      businessType: "Business type",
      documents: "Verification documents",
      requiredFor: (type: string) => `Required for ${type}.`,
      chooseTypeFirst: "Choose a business type to see the documents it needs.",
      submit: "Submit for verification",
      submitting: "Submitting…",
      cancel: "Cancel conversion",
      missing: (n: number) => `${n} document(s) missing`,
      siretInvalid: "SIRET must have 14 digits",
    },
    modals: {
      upgradeTitle: "You have reached your subscription limit.",
      upgradeText: (plan: string, limit: number, live: number) =>
        `Your ${plan} plan includes ${limit} active listings (${live} live). Your existing listings stay online.`,
      upgradeOk: (plan: string, price: string) => `Switch to ${plan} — ${price}/month`,
      later: "Later",
      urgentExtendTitle: "Extend Urgent",
      urgentMakeTitle: "Make Urgent",
      urgentText: "Increase your vehicle’s visibility to VTC drivers.",
      urgentExtendOk: "Extend",
      urgentActivateOk: "Activate Urgent",
      urgentActivated: (date: string) => `URGENT ACTIVE — expires on ${date}`,
      urgentActiveUntil: (date: string) => `URGENT ACTIVE · expires on ${date}`,
      urgentOption: (days: number, price: string) => `${days} days — ${price}`,
      until: (date: string) => `until ${date}`,
      autoRenew: "Renew automatically at expiry",
      payNote: (card: string) =>
        `One-off payment charged to ${card}. No automatic renewal unless you tick the box. Simulation, no payment provider connected.`,
      savedCard: "your saved card",
      blockTitle: (code: string) => `Block ${code}`,
      blockText:
        "The vehicle will be unavailable during this period. Not possible if a confirmed booking overlaps.",
      blockOk: "Block these dates",
      blocked: "Dates blocked",
      from: "From",
      to: "To",
      reasonPlaceholder: "Reason (service, bodywork…)",
    },
    comingSoon: {
      title: "Coming soon",
      notBuilt: " isn't built yet.",
    },
    status: {
      booking: {
        pending: "Pending",
        documents_required: "Documents required",
        confirmed: "Confirmed",
        active: "Rental in progress",
        completed: "Completed",
        declined: "Declined",
        cancelled: "Cancelled",
        expired: "Expired",
      },
      publish: {
        draft: "Draft",
        published: "Published",
        paused: "Paused",
        moderated: "Under moderation",
        archived: "Archived",
      },
      unit: {
        active: "In service",
        maintenance: "Maintenance",
        retired: "Retired",
      },
      driverDoc: {
        approved: "Approved",
        pending: "Under review",
        missing: "Missing",
        expired: "Expired",
      },
      subscription: {
        trial: "Free trial",
        active: "Active",
        past_due: "Payment overdue",
        cancelled: "Cancelled",
        expired: "Expired",
      },
      verification: {
        incomplete: "Verification to complete",
        pending: "Verification pending",
        verified: "Verified",
        rejected: "Rejected",
      },
      companyDoc: {
        approved: "Approved",
        pending: "Under review",
        rejected: "Rejected",
        missing: "Missing",
      },
      driver: {
        verified: "Verified",
        pending: "Pending",
      },
      invoice: {
        Paid: "Paid",
        Failed: "Failed",
      },
    },
    plans: {
      free: { name: "Free", features: ["Booking requests", "Availability calendar", "1 user"] },
      pro: { name: "Pro", features: ["Everything in Free", "Messaging with drivers", "Up to 3 users"] },
      business: { name: "Business", features: ["Everything in Pro", "Priority support", "Up to 8 users"] },
      fleet: { name: "Fleet", features: ["Everything in Business", "Dedicated account manager", "Up to 20 users"] },
    },
    docTypes: {
      vtc: "VTC card",
      licence: "Driving licence",
      id: "ID document",
      address: "Proof of address",
    },
  },
};
